import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { artProxyUrl, loadArtworkTexture } from '../artwork-texture.js';

// Contrat du rig partagé : interdit. La pochette est toute la scène.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'pixelsort',
  family: 'graphic',
  danceability: 0.84,
  requiresTier: 0,
  energyLevel: 0.80,
  bpmMin: 100,
  bpmMax: 170,
  movement: 0.75,
  tonality: 0.50,
  enabled: true
};

// Pixelsort : la pochette en grand, passée au TRI DE PIXELS du glitch art -
// dans des bandes, les pixels sont triés par luminosité et coulent en longues
// traînées. LOOK VDJ party. Électro, techno, IDM, hip-hop expérimental, bass
// music.
//
// LE FOND : la pochette recadrée en 16:9 puis agrandie pour COUVRIR l'écran
// (R18, échelle max), centrée sur l'écran (R19 : jamais sur la pochette DOM),
// légèrement floue (biais de mip 2), assombrie à 40 %.
//
// LE TRI (R17) : dans un Web Worker, au changement de morceau et une phrase
// À L'AVANCE (le mode suivant est prêt quand la phrase tombe : la coupe est
// exacte). Sur la découpe 16:9 en 320 x 180, pour chaque ligne de la GRILLE
// du mode, les INTERVALLES de pixels plus clairs qu'un seuil (le 35e centile
// de luminance de la pochette, pour qu'une pochette sombre trie aussi) sont
// triés par luminosité croissante dans le sens du tri. Les grilles :
//   ROWS      lignes de l'image, tri vers la droite
//   COLUMNS   colonnes, tri vers le bas (la pochette fond)
//   DIAGONAL  lignes d'une grille tournée de 45°
//   RADIAL    rayons depuis le centre (grille polaire 200 rayons x 360 angles)
//   MELT      colonnes triées EN ENTIER (sans seuil), un front qui descend en
//             8 mesures
// RADIAL est toujours gardé prêt (le drop).
//
// LE RENDU, un seul quad : la pochette floue d'un côté, la version triée de
// l'autre ; un masque de BANDES procédural (perpendiculaires au sens du tri ;
// en RADIAL, des secteurs) choisit, bords nets (coupe glitch, sans fondu).
// Dans une bande, la lecture triée est décalée le long du sens du tri d'un
// offset qui avance : les traînées glissent. Séparation RGB plus forte dans
// les bandes, décalage de bloc sur certaines mesures, ligne de balayage sur
// la phrase.
//
// Sans pochette : un dégradé procédural de la palette par défaut, trié pareil.
//
// Debug : ?sortmode=rows|columns|diagonal|radial|melt, ?sortall=1 (tout
// l'écran trié), ?sortview=1 (la texture triée seule, sans coulée),
// ?sortdemo=1 (le dégradé procédural du repli, même avec une pochette).

export const MODES = ['rows', 'columns', 'diagonal', 'radial', 'melt'];
const M_RADIAL = 3, M_MELT = 4;
const SW = 320, SH = 180;
const MAX_BANDS = 8;
const DARKEN = 0.40;
const BLUR_BIAS = 2.0;
const SAFE_FLOOR = 0.5;
const NO_BPM = 124;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const MELT_BARS = 8;
const FALLBACK = [0xff2bd6, 0x2bffea, 0xffc21a];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- the worker: pixel sorting on a 320 x 180 crop ------------------------------
// self-contained (stringified into a Blob): no import, no closure
function sortGrid(src, W, H, mode, thr) {
  var GW, GH, i, j;
  var cx = W / 2, cy = H / 2;
  var L = Math.ceil(Math.sqrt(W * W + H * H));
  var Rmax = Math.sqrt(cx * cx + cy * cy);
  if (mode === 0) { GW = W; GH = H; }
  else if (mode === 1 || mode === 4) { GW = H; GH = W; }
  else if (mode === 2) { GW = L; GH = L; }
  else { GW = 200; GH = 360; }
  var out = new Uint8Array(GW * GH * 4);
  var r = new Float32Array(GW), g = new Float32Array(GW), b = new Float32Array(GW), lum = new Float32Array(GW), ok = new Uint8Array(GW);
  var idx = new Array(GW);
  var c45 = Math.SQRT1_2;
  for (j = 0; j < GH; j++) {
    // the row of the grid: sample the crop (bilinear), mark what falls outside
    for (i = 0; i < GW; i++) {
      var x, y;
      if (mode === 0) { x = i; y = j; }
      else if (mode === 1 || mode === 4) { x = j; y = i; }
      else if (mode === 2) { var u = i - L / 2, v = j - L / 2; x = cx + c45 * u - c45 * v; y = cy + c45 * u + c45 * v; }
      else { var a = 2 * Math.PI * (j + 0.5) / GH, rr = (i + 0.5) / GW * Rmax; x = cx + rr * Math.cos(a) - 0.5; y = cy + rr * Math.sin(a) - 0.5; }
      if (x < 0 || y < 0 || x > W - 1 || y > H - 1) { ok[i] = 0; r[i] = g[i] = b[i] = lum[i] = 0; continue; }
      var x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0, x1 = Math.min(W - 1, x0 + 1), y1 = Math.min(H - 1, y0 + 1);
      var p00 = (y0 * W + x0) * 4, p10 = (y0 * W + x1) * 4, p01 = (y1 * W + x0) * 4, p11 = (y1 * W + x1) * 4;
      var w00 = (1 - fx) * (1 - fy), w10 = fx * (1 - fy), w01 = (1 - fx) * fy, w11 = fx * fy;
      r[i] = src[p00] * w00 + src[p10] * w10 + src[p01] * w01 + src[p11] * w11;
      g[i] = src[p00 + 1] * w00 + src[p10 + 1] * w10 + src[p01 + 1] * w01 + src[p11 + 1] * w11;
      b[i] = src[p00 + 2] * w00 + src[p10 + 2] * w10 + src[p01 + 2] * w01 + src[p11 + 2] * w11;
      lum[i] = (0.2126 * r[i] + 0.7152 * g[i] + 0.0722 * b[i]) / 255;
      ok[i] = 1;
    }
    // the intervals over the threshold (MELT: the whole valid run), sorted
    // by luminance, ascending in the sort's direction
    i = 0;
    while (i < GW) {
      if (!ok[i] || (mode !== 4 && lum[i] <= thr)) {
        var o = (j * GW + i) * 4;
        out[o] = r[i]; out[o + 1] = g[i]; out[o + 2] = b[i]; out[o + 3] = ok[i] ? 255 : 0;
        i++; continue;
      }
      var s = i;
      while (i < GW && ok[i] && (mode === 4 || lum[i] > thr)) i++;
      var n = i - s;
      for (var k = 0; k < n; k++) idx[k] = s + k;
      var part = idx.slice(0, n).sort(function (p, q) { return lum[p] - lum[q]; });
      for (k = 0; k < n; k++) {
        var src_i = part[k], oo = (j * GW + s + k) * 4;
        out[oo] = r[src_i]; out[oo + 1] = g[src_i]; out[oo + 2] = b[src_i]; out[oo + 3] = 255;
      }
    }
  }
  return { w: GW, h: GH, data: out };
}
function sortWorkerSource() {
  return 'var sortGrid = ' + sortGrid.toString() + ';\n' +
    'self.onmessage = function (ev) {\n' +
    '  var m = ev.data;\n' +
    '  try {\n' +
    '    var t0 = performance.now();\n' +
    '    var res = sortGrid(m.pixels, m.w, m.h, m.mode, m.thr);\n' +
    '    res.ms = performance.now() - t0;\n' +
    '    self.postMessage({ id: m.id, mode: m.mode, res: res }, [res.data.buffer]);\n' +
    '  } catch (e) { self.postMessage({ id: m.id, mode: m.mode, error: String(e && e.message || e) }); }\n' +
    '};\n';
}
let _worker = null, _workerFailed = false, _nextId = 0;
const _waiting = new Map();
function sharedWorker() {
  if (_worker || _workerFailed) return _worker;
  if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') { _workerFailed = true; return null; }
  try {
    const url = URL.createObjectURL(new Blob([sortWorkerSource()], { type: 'text/javascript' }));
    _worker = new Worker(url);
    _worker.onmessage = (ev) => { const cb = _waiting.get(ev.data.id); if (cb) { _waiting.delete(ev.data.id); cb(ev.data); } };
    _worker.onerror = () => { for (const [id, cb] of _waiting) cb({ id, error: 'worker' }); _waiting.clear(); };
  } catch (e) { _workerFailed = true; _worker = null; }
  return _worker;
}

// --- shader ---------------------------------------------------------------------
const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const fragmentShader = `
  precision highp float;
  uniform sampler2D uCover;
  uniform sampler2D uSorted;
  uniform float uMode;
  uniform float uAspect;           // the screen's
  uniform float uCropAspect;       // the cover's 16:9 crop, in the cover's uv
  uniform vec2 uCropScale;         // the crop inside the (square) cover texture
  uniform vec4 uBand[${MAX_BANDS}];// centre, half-width, flow direction (+-1), on
  uniform float uFlow;             // the flow's offset, texture units
  uniform float uAll;              // 1: the whole screen sorted
  uniform float uMelt;             // MELT's front, 0..1 from the top
  uniform float uView;             // debug: the sorted texture alone
  uniform float uDarken;
  uniform float uClean;            // the drop's clean cover (1 = sharp, full)
  uniform float uChroma;           // the beat's RGB split, px
  uniform vec4 uBlock;             // y0, y1 (cover uv, from the top), shift (screen fraction), on
  uniform float uScan;             // the scanline's y (from the top), < 0 off
  uniform float uPx;               // a screen pixel, in screen uv (y)
  uniform float uExposure;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  const float W = ${SW.toFixed(1)}, H = ${SH.toFixed(1)};
  const float TAU = 6.28318531;

  // the grid coordinates of the mode for a crop point c (crop uv, y from the top)
  // -> (u along the sort, v across it, band coordinate s)
  vec3 gridOf(vec2 c) {
    vec2 p = c * vec2(W, H);
    if (uMode < 0.5) return vec3(c.x, c.y, c.y);
    if (uMode < 1.5 || uMode > 3.5) return vec3(c.y, c.x, c.x);
    if (uMode < 2.5) {
      float L = ceil(sqrt(W * W + H * H));
      vec2 d = p - vec2(W, H) * 0.5;
      vec2 q = vec2(0.70710678 * (d.x + d.y), 0.70710678 * (-d.x + d.y)) + L * 0.5;
      return vec3(q.x / L, q.y / L, q.y / L);
    }
    vec2 d = p - vec2(W, H) * 0.5;
    float Rmax = length(vec2(W, H) * 0.5);
    float a = fract(atan(d.y, d.x) / TAU);
    return vec3(length(d) / Rmax, a, a);
  }
  vec4 sorted(vec3 g, float off) {
    // the sorted texture: u along the sort (flowing), v across
    float u = g.x - off;
    u = uMode > 2.5 && uMode < 3.5 ? fract(u) : fract(u);
    return texture2D(uSorted, vec2(u, g.y));
  }
  float safeAtt(vec2 uv) {
    vec2 s = (uv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  void main() {
    // R18: the 16:9 crop COVERS the screen, centred on the screen (R19)
    vec2 sc = vUv - 0.5;
    if (uAspect > uCropAspect) sc.y *= uCropAspect / uAspect; else sc.x *= uAspect / uCropAspect;
    vec2 c = sc + 0.5;                                  // crop uv, y up
    // the glitch block: a strip shifted sideways for a beat
    if (uBlock.w > 0.5 && 1.0 - c.y > uBlock.x && 1.0 - c.y < uBlock.y) c.x = fract(c.x + uBlock.z);
    vec2 ct = vec2(c.x, 1.0 - c.y);                    // y from the top
    vec2 tc = 0.5 + (vec2(c.x, c.y) - 0.5) * uCropScale;   // into the square cover
    vec3 g = gridOf(ct);
    if (uView > 0.5) { gl_FragColor = vec4(texture2D(uSorted, vec2(g.x, g.y)).rgb, 1.0); return; }
    // the band mask: sharp edges, no fade
    float on = uAll;
    float dir = 1.0;
    for (int k = 0; k < ${MAX_BANDS}; k++) {
      vec4 b = uBand[k];
      if (b.w < 0.5) continue;
      float ds = abs(g.z - b.x);
      if (uMode > 2.5 && uMode < 3.5) ds = min(ds, 1.0 - ds);   // the angle wraps
      if (ds < b.y) { on = 1.0; dir = b.z; }
    }
    if (uMode > 3.5) on = max(on, step(ct.y, uMelt));
    vec3 base = texture2D(uCover, tc, ${BLUR_BIAS.toFixed(1)}).rgb * uDarken;
    vec3 col = base;
    if (on > 0.5 && uClean < 0.5) {
      float off = uFlow * dir;
      // the RGB split, stronger in the bands: along the sort
      float ch = uChroma * 1.5 / W;
      vec4 sm = sorted(g, off);
      vec3 s3 = vec3(sorted(g, off - ch).r, sm.g, sorted(g, off + ch).b);
      if (sm.a > 0.5) col = s3;                        // outside the grid (corners): the cover
    }
    if (uClean > 0.5) col = texture2D(uCover, tc).rgb;   // the drop: the cover, sharp
    // the scanline of the phrase: 2 px, light
    if (uScan >= 0.0) {
      float d = abs((1.0 - vUv.y) - uScan) / uPx;
      col += vec3(0.55) * (1.0 - smoothstep(0.5, 1.5, d)) + vec3(0.12) * exp(-d / 6.0);
    }
    gl_FragColor = vec4(col * uExposure * safeAtt(vUv), 1.0);
  }
`;

let MAT = null;
function material() {
  if (MAT) return MAT;
  const bands = [];
  for (let i = 0; i < MAX_BANDS; i++) bands.push(new THREE.Vector4(0, 0, 1, 0));
  MAT = new THREE.ShaderMaterial({
    uniforms: {
      uCover: { value: null }, uSorted: { value: null }, uMode: { value: 0 },
      uAspect: safeZoneUniforms.uAspect, uCropAspect: { value: 16 / 9 }, uCropScale: { value: new THREE.Vector2(1, 9 / 16) },
      uBand: { value: bands }, uFlow: { value: 0 }, uAll: { value: 0 }, uMelt: { value: 0 }, uView: { value: 0 },
      uDarken: { value: DARKEN }, uClean: { value: 0 }, uChroma: { value: 0 },
      uBlock: { value: new THREE.Vector4(0, 0, 0, 0) }, uScan: { value: -1 }, uPx: { value: 1 / 720 }, uExposure: { value: 1 },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    },
    vertexShader: quadVertexShader, fragmentShader, depthTest: false, depthWrite: false
  });
  return MAT;
}

export class PixelsortVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    const m = MODES.indexOf(urlParam('sortmode'));
    this._forceMode = m >= 0 ? m : null;
    this._forceAll = urlParam('sortall') === '1';
    this._view = urlParam('sortview') === '1';
    this._demo = urlParam('sortdemo') === '1';        // the procedural fallback, cover or not
    // glitch: no bloom (a sorted cover full of light would veil), no tone map
    this._post = new VdjPost({ bloomStrength: 0, bloomThreshold: 0.9, grain: 0.014, vignette: 0.30, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    sharedWorker();
    this._mat = material(); this._u = this._mat.uniforms;
    this._blank = new THREE.DataTexture(new Uint8Array([20, 20, 24, 255]), 1, 1); this._blank.needsUpdate = true;
    this._u.uCover.value = this._blank; this._u.uSorted.value = this._blank;
    this._u.uView.value = this._view ? 1 : 0;
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._size = new THREE.Vector2();
    this._display.onBeforeRender = function (renderer) { renderer.getDrawingBufferSize(self._size); self._u.uPx.value = 1 / Math.max(1, self._size.y); };
    scene.add(this._display);

    this._time = 0; this._beats = 0; this._beatPeriod = 60 / NO_BPM;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrasePhase = null; this._free = 0;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0;
    this._mode = this._forceMode != null ? this._forceMode : 0;
    this._bands = [];
    this._flow = 0; this._flowBoost = 0;
    this._build = 0; this._energy = 0.6;
    this._dropBeat = -99; this._meltStart = 0; this._block = null; this._scanT = -1;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastDropKey = null;
    this._coverUrl = undefined; this._coverTex = null; this._pixels = null; this._thr = 0.3; this._meanLum = 0.3;
    this._sortedTex = {}; this._pendingMode = new Set(); this._sortMs = [];
    this._seed = 9876543;
    this._useFallback();
  }

  _rnd() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  // --- the source pixels: the cover's 16:9 centre crop at 320 x 180 ------------
  _loadCover(profile) {
    const url = this._demo ? null : (profile.artworkUrl || null);
    if (url === this._coverUrl) return;
    this._coverUrl = url;
    if (!url) { this._useFallback(); return; }
    const self = this;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._coverUrl) { tex.dispose(); return; }
      if (self._coverTex) self._coverTex.dispose();
      self._coverTex = tex; self._u.uCover.value = tex;
      const a = tex.image && tex.image.width ? tex.image.width / tex.image.height : 1;
      self._setCrop(a);
    }, () => {});
    if (typeof Image === 'undefined' || typeof document === 'undefined') return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (!self._scene || url !== self._coverUrl) return;
      try {
        const cv = document.createElement('canvas'); cv.width = SW; cv.height = SH;
        const g = cv.getContext('2d');
        // the 16:9 centre crop of the cover
        const iw = img.naturalWidth, ih = img.naturalHeight, ca = SW / SH;
        let sw = iw, sh = iw / ca; if (sh > ih) { sh = ih; sw = ih * ca; }
        g.drawImage(img, (iw - sw) / 2, (ih - sh) / 2, sw, sh, 0, 0, SW, SH);
        self._setPixels(g.getImageData(0, 0, SW, SH).data);
      } catch (e) { self._useFallback(); }
    };
    img.onerror = () => { if (url === self._coverUrl) self._useFallback(); };
    img.src = artProxyUrl(url);
  }
  // no cover: a gradient of the default palette, in the same 320 x 180
  _useFallback() {
    const px = new Uint8ClampedArray(SW * SH * 4);
    const cols = FALLBACK.map((h) => vdjColor(h).convertLinearToSRGB());
    for (let y = 0; y < SH; y++) for (let x = 0; x < SW; x++) {
      const t = (x / SW + 0.35 * Math.sin(y / SH * 3.1 + x / SW * 2.0)) * 2;
      const k = ((t % 3) + 3) % 3, i0 = Math.floor(k), f = k - i0, a = cols[i0], b = cols[(i0 + 1) % 3];
      const v = 0.25 + 0.75 * (0.5 + 0.5 * Math.sin(x * 0.09 + y * 0.05));
      const o = (y * SW + x) * 4;
      px[o] = 255 * (a.r + (b.r - a.r) * f) * v; px[o + 1] = 255 * (a.g + (b.g - a.g) * f) * v; px[o + 2] = 255 * (a.b + (b.b - a.b) * f) * v; px[o + 3] = 255;
    }
    if (this._fallbackTex) this._fallbackTex.dispose();
    this._fallbackTex = new THREE.DataTexture(new Uint8Array(px.buffer), SW, SH, THREE.RGBAFormat);
    this._fallbackTex.colorSpace = THREE.SRGBColorSpace; this._fallbackTex.flipY = true;
    this._fallbackTex.minFilter = THREE.LinearMipmapLinearFilter; this._fallbackTex.magFilter = THREE.LinearFilter; this._fallbackTex.generateMipmaps = true;
    this._fallbackTex.needsUpdate = true;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._u.uCover.value = this._fallbackTex;
    this._setCrop(SW / SH);
    this._setPixels(px);
  }
  // the cover texture is square (or the fallback 16:9): the 16:9 crop in its uv
  _setCrop(texAspect) {
    const ca = SW / SH;
    if (texAspect >= ca) this._u.uCropScale.value.set(ca / texAspect, 1);
    else this._u.uCropScale.value.set(1, texAspect / ca);
    this._u.uCropAspect.value = ca;
  }
  _setPixels(px) {
    this._pixels = new Uint8Array(px.buffer.slice(0));
    // the threshold: the 35th centile of luminance (a dark cover sorts too)
    const l = new Float32Array(SW * SH);
    let sum = 0;
    for (let i = 0; i < SW * SH; i++) { l[i] = (0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2]) / 255; sum += l[i]; }
    this._meanLum = sum / (SW * SH);
    const s = Array.from(l).sort((a, b) => a - b);
    this._thr = Math.max(0.08, s[Math.floor(s.length * 0.35)]);
    this._sortedTex = {}; this._pendingMode.clear();
    this._request(this._mode); this._request(M_RADIAL);
    this._request(this._nextMode());
  }
  _nextMode() { return this._forceMode != null ? this._forceMode : (this._mode + 1) % MODES.length; }
  _request(mode) {
    if (!this._pixels || this._sortedTex[mode] || this._pendingMode.has(mode)) return;
    this._pendingMode.add(mode);
    const id = ++_nextId, self = this, key = this._coverUrl;
    const done = (msg) => {
      self._pendingMode.delete(mode);
      if (!self._scene || key !== self._coverUrl || msg.error) return;
      const r = msg.res;
      self._sortMs.push(+r.ms.toFixed(1));
      const t = new THREE.DataTexture(r.data, r.w, r.h, THREE.RGBAFormat);
      t.colorSpace = THREE.SRGBColorSpace; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
      t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.ClampToEdgeWrapping; t.generateMipmaps = false; t.needsUpdate = true;
      if (self._sortedTex[mode]) self._sortedTex[mode].dispose();
      self._sortedTex[mode] = t;
    };
    const w = sharedWorker();
    const msg = { id, pixels: this._pixels.slice(0), w: SW, h: SH, mode, thr: mode === M_MELT ? -1 : this._thr };
    if (w) { _waiting.set(id, done); w.postMessage(msg, [msg.pixels.buffer]); }
    else {
      // no worker (never seen in a browser): sort on the main thread, late
      setTimeout(() => { try { const t0 = performance.now(); const res = sortGrid(msg.pixels, SW, SH, mode, msg.thr); res.ms = performance.now() - t0; done({ res }); } catch (e) { done({ error: String(e) }); } }, 0);
    }
  }

  // --- rhythm -------------------------------------------------------------------
  _onBeat() {
    this._beatCount++;
    this._flowBoost = 1;
    // new active bands: 2 to 6, more in the build; each lives a beat or two
    const n = Math.round(2 + 4 * Math.max(this._energy * 0.6, this._build));
    const t = this._time;
    this._bands = this._bands.filter((b) => b.until > t);
    for (let k = 0; k < n && this._bands.length < MAX_BANDS; k++) {
      const w = (0.012 + 0.07 * this._rnd() * this._rnd()) * (1 + 3 * this._build);
      this._bands.push({ c: this._rnd(), w, v: (this._rnd() - 0.5) * 0.08, dir: this._rnd() < 0.7 ? 1 : -1, until: t + this._beatPeriod * (1 + Math.floor(this._rnd() * 2)) });
    }
  }
  _onBar() {
    this._barCount++;
    // on some bars, a strip slips sideways by a block (8 to 40 px)
    if (this._rnd() < 0.5) {
      const y0 = this._rnd() * 0.85, h = 0.03 + 0.1 * this._rnd();
      const px = 8 + 32 * this._rnd();
      this._block = { y0, y1: y0 + h, shift: (this._rnd() < 0.5 ? -1 : 1) * px / Math.max(1, this._size.x || 1280), until: this._time + this._beatPeriod };
    }
  }
  _onPhrase() {
    this._phraseCount++;
    if (this._forceMode == null) {
      this._mode = (this._mode + 1) % MODES.length;
      if (this._mode === M_MELT) this._meltStart = this._beats;
    }
    this._scanT = 0;                                  // the scanline crosses the screen
    this._request(this._mode); this._request(this._nextMode());     // the next phrase, ahead
  }
  _onDrop(director) {
    this._dropBeat = this._beats;
    if (director && this._comfort.requestFlash(this._time)) director.flash = Math.max(director.flash || 0, VDJ_DROP_FLASH);
    this._bands = [];
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._loadCover(profile); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    this._loadCover(profile);
    if (profile.playbackState !== 'PLAYING') return;     // the frame holds
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    this._beatPeriod = 60 / bpm;
    const energy = director && director.intensity != null ? clamp01(director.intensity) : 0.6;
    this._energy += (energy - this._energy) * Math.min(1, dt / 1.5);

    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) { if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true; this._lastBeatPhase = beatPhase; }
    else { this._free += dt; if (this._free >= this._beatPeriod) { this._free -= this._beatPeriod; beatEdge = true; } }
    this._beats += dt / this._beatPeriod;
    const ev = director && director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const build = beatsToDrop != null && beatsToDrop < 32 ? clamp01((32 - beatsToDrop) / 30) : (director && director.mode === 'build' ? 0.5 : 0);
    this._build += (build - this._build) * Math.min(1, dt / 0.5);

    if (beatEdge) this._onBeat();
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar(); this._lastBarPhase = barPhase; }
    else if (beatEdge && this._beatCount % 4 === 1 && this._beatCount > 1) this._onBar();
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase(); this._lastPhrasePhase = phrasePhase; }
    else if (beatEdge && this._beatCount % 32 === 1 && this._beatCount > 1) this._onPhrase();
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    let drop = false;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey) { this._lastDropKey = key; drop = true; }
    } else if (beatPhase != null && this._tickDrops(profile)) drop = true;
    if (drop) this._onDrop(director);

    // the flow: steady, a kick of speed on each beat
    this._flowBoost = Math.max(0, this._flowBoost - dt / (this._beatPeriod * 0.4));
    this._flow += dt * (0.05 + 0.25 * this._flowBoost * this._flowBoost + 0.15 * this._build);
    for (const b of this._bands) b.c = ((b.c + b.v * dt) % 1 + 1) % 1;

    // the drop: one beat of RADIAL everywhere, then 2 beats of the clean cover
    const sd = this._beats - this._dropBeat;
    const dropRadial = sd >= 0 && sd < 1, clean = sd >= 1 && sd < 3;
    const mode = dropRadial ? M_RADIAL : this._mode;
    const tex = this._sortedTex[mode];
    const u = this._u;
    u.uSorted.value = tex || this._blank;
    u.uMode.value = mode;
    u.uFlow.value = this._flow;
    u.uAll.value = this._forceAll || dropRadial || this._build > 0.92 ? 1 : 0;
    u.uClean.value = clean ? 1 : 0;
    u.uMelt.value = mode === M_MELT ? clamp01((this._beats - this._meltStart) / (MELT_BARS * 4)) : 0;
    const t = this._time;
    for (let k = 0; k < MAX_BANDS; k++) {
      const b = this._bands[k], v = u.uBand.value[k];
      if (b && b.until > t && tex) v.set(b.c, b.w, b.dir, 1); else v.set(0, 0, 1, 0);
    }
    if (!tex) u.uAll.value = 0;
    const chroma = vdjBeatChroma(beatPhase != null ? beatPhase : this._beats % 1, this._beatPeriod);
    this._post.update(chroma);
    u.uChroma.value = (chroma - 1) * 1.2;              // px of the 320 grid, in the bands
    if (this._block && this._block.until > t) u.uBlock.value.set(this._block.y0, this._block.y1, this._block.shift, 1);
    else u.uBlock.value.set(0, 0, 0, 0);
    if (this._scanT >= 0) { this._scanT += dt / this._beatPeriod; u.uScan.value = this._scanT <= 1 ? this._scanT : -1; if (this._scanT > 1) this._scanT = -1; }
    else u.uScan.value = -1;
    // comfort: the cover's mean luminance, darkened outside the bands
    let frac = u.uAll.value;
    if (!frac) { let s = 0; for (const b of this._bands) s += 2 * b.w; frac = Math.min(1, s); }
    const est = this._meanLum * (DARKEN * (1 - frac) + frac) + (director && director.flash ? director.flash * 0.8 : 0);
    u.uExposure.value = this._comfort.measure(est, dt);
  }

  get probe() {
    return {
      t: +this._time.toFixed(2), mode: MODES[this._u.uMode.value], bands: this._bands.length, all: this._u.uAll.value, clean: this._u.uClean.value,
      ready: Object.keys(this._sortedTex).map((k) => MODES[k]), sortMs: this._sortMs.slice(-6), thr: +this._thr.toFixed(3), build: +this._build.toFixed(2),
      melt: +this._u.uMelt.value.toFixed(3), exposure: +this._u.uExposure.value.toFixed(3), cover: !!this._coverTex
    };
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); this._display.geometry.dispose(); }
    for (const k of Object.keys(this._sortedTex)) this._sortedTex[k].dispose();
    this._sortedTex = {};
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    if (this._fallbackTex) { this._fallbackTex.dispose(); this._fallbackTex = null; }
    if (this._blank) this._blank.dispose();
    this._u.uCover.value = null; this._u.uSorted.value = null;
    this._scene = null;
  }
}
